import React, { useState, useRef } from "react";
import Slider from "react-slick";
import "./Testimonials.scss";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { Helmet } from "react-helmet";

const Testimonials = ({ homepage }) => {
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const sliderRef = useRef(null);

  if (
    !homepage?.fifthBlock?.testimonials ||
    homepage?.fifthBlock?.testimonials.length === 0
  ) {
    return null;
  }

  const nextTestimonial = () => {
    if (
      sliderRef.current &&
      sliderRef.current.slickNext &&
      currentTestimonialIndex < homepage?.fifthBlock?.testimonials.length - 1
    ) {
      sliderRef.current.slickNext();
    }
  };

  const prevTestimonial = () => {
    if (
      sliderRef.current &&
      sliderRef.current.slickPrev &&
      currentTestimonialIndex > 0
    ) {
      sliderRef.current.slickPrev();
    }
  };

  const goToTestimonial = (index) => {
    if (
      sliderRef.current &&
      sliderRef.current.slickGoTo &&
      index >= 0 &&
      index < homepage?.fifthBlock?.testimonials.length
    ) {
      sliderRef.current.slickGoTo(index);
    }
  };

  // Générer les données structurées pour les avis Google
  const generateStructuredData = () => {
    if (
      !homepage?.fifthBlock?.testimonials ||
      homepage?.fifthBlock?.testimonials.length === 0
    ) {
      return null;
    }

    const testimonials = homepage.fifthBlock.testimonials;

    // Calculer la note moyenne
    const totalRating = testimonials.reduce((sum, t) => sum + (t.note || 0), 0);
    const averageRating =
      testimonials.length > 0 ? totalRating / testimonials.length : 0;
    const roundedAverage = Math.round(averageRating * 10) / 10;

    // Créer les avis individuels
    const reviews = testimonials.map((testimonial) => ({
      "@type": "Review",
      author: {
        "@type": "Person",
        name: testimonial.title || "Client",
      },
      reviewBody: testimonial.description || "",
      reviewRating: {
        "@type": "Rating",
        ratingValue: testimonial.note || 5,
        bestRating: 5,
        worstRating: 1,
      },
      datePublished: new Date().toISOString().split("T")[0],
    }));

    // Structure pour Organization avec AggregateRating
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Normandie Nuisibles",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: roundedAverage,
        bestRating: 5,
        worstRating: 1,
        ratingCount: testimonials.length,
        reviewCount: testimonials.length,
      },
      review: reviews,
    };
  };

  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    waitForAnimate: true,
    autoplay: false,
    autoplaySpeed: 4000,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
    arrows: false,
    beforeChange: (oldIndex, newIndex) => {
      setCurrentTestimonialIndex(newIndex);
    },
    variableWidth: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: true,
          dots: true,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          dots: true,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          dots: true,
        },
      },
    ],
  };

  const structuredData = generateStructuredData();

  return (
    <>
      {structuredData && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        </Helmet>
      )}
      <section className="section testimonials-block">
        <div className="inner">
          <div className="testimonials-content">
            <div className="testimonials-outer">
              <div className="testimonials-text m-padding">
                <div
                  dangerouslySetInnerHTML={{
                    __html: homepage?.fifthBlock?.text,
                  }}
                />
                {/* Boutons de navigation personnalisés */}
                <div className="custom-navigation">
                  <button
                    className={`custom-nav-btn prev ${
                      currentTestimonialIndex === 0 ? "disabled" : ""
                    }`}
                    onClick={prevTestimonial}
                    disabled={currentTestimonialIndex === 0}
                    aria-label="Témoignage précédent"
                  >
                    <ArrowLeftIcon
                      style={{
                        width: "18px",
                      }}
                    />
                  </button>
                  <button
                    className={`custom-nav-btn next ${
                      currentTestimonialIndex ===
                      homepage?.fifthBlock?.testimonials.length - 1
                        ? "disabled"
                        : ""
                    }`}
                    onClick={nextTestimonial}
                    disabled={
                      currentTestimonialIndex ===
                      homepage?.fifthBlock?.testimonials.length - 1
                    }
                    aria-label="Témoignage suivant"
                  >
                    <ArrowRightIcon
                      style={{
                        width: "18px",
                      }}
                    />
                  </button>
                </div>
              </div>
              <div className="testimonials-cards">
                <div className="testimonials-carousel">
                  <Slider ref={sliderRef} {...settings}>
                    {homepage?.fifthBlock?.testimonials.map(
                      (testimonial, index) => (
                        <div className="testimonial-card" key={index}>
                          <div className="testimonial-top">
                            <div className="testimonial-header">
                              <h4>{testimonial.title}</h4>
                              <p className="testimonial-title">
                                {testimonial.subtitle}
                              </p>
                            </div>
                            <p className="testimonial-quote">
                              {testimonial.description}
                            </p>
                          </div>
                          <div className="testimonial-rating">
                            {[...Array(5)].map((_, starIndex) => (
                              <span
                                key={starIndex}
                                className={`star ${
                                  starIndex < testimonial.note ? "filled" : ""
                                }`}
                              >
                                ★
                              </span>
                            ))}
                          </div>
                        </div>
                      )
                    )}
                  </Slider>
                </div>
              </div>
            </div>
            {homepage?.fifthBlock?.logos &&
              homepage?.fifthBlock?.logos.length > 0 && (
                <div className="partners-logos m-padding">
                  {homepage?.fifthBlock?.logos.map((logo, index) => (
                    <div className="logo" key={index}>
                      <img
                        src={logo?.logo?.localFile?.childImageSharp?.fluid?.src}
                        alt={logo?.logo?.altText}
                      />
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Testimonials;
